import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { t } from '@hefe/i18n';
import { BasketballGame, EventReportEventableType } from '@hefe/models';
import {
  currentUserIsAdminSelector,
  currentUserSelector,
  EventReportsApi,
  ModalsSlice,
} from '@hefe/store';
import {
  formatDateTime,
  fullUserName,
  useUserBasketballGameRoles,
} from '@hefe/utils';
import { Button, Divider, Popconfirm, Skeleton, Typography } from 'antd';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import HtmlParser from '../../../core/utils/html-parser/HtmlParser';
import AddEditEventReportForm from '../../forms/add-edit-event-report/add-edit-event-report.form';

interface EventReportComponentProps {
  game: BasketballGame;
  reportId?: number;
  onDeleted?: () => void;
}

export default function EventReportComponent(props: EventReportComponentProps) {
  const { reportId } = props;

  const dispatch = useAppDispatch();

  const user = useSelector(currentUserSelector);
  const userIsAdmin = useSelector(currentUserIsAdminSelector);

  const { userIsCoach } = useUserBasketballGameRoles({
    game: props.game,
    user,
  });

  const { data, isFetching } = EventReportsApi.useFetchReportQuery(
    reportId ?? 0,
    { skip: !reportId }
  );

  const [deleteReport, { isLoading: isDeleting }] =
    EventReportsApi.useDeleteReportMutation();

  const onEdit = () => {
    props.onDeleted?.();
    dispatch(
      ModalsSlice.modalsActions.openModal({
        formId: `add-edit-event-report-form-${data?.id}`,
        component: (
          <AddEditEventReportForm
            game={props.game}
            report={data}
            eventId={data?.eventable_id ?? 0}
            eventType={
              data?.eventable_type ??
              EventReportEventableType.BASKETBALL_CLUB_GAME
            }
            groupType={data?.groupable_type}
            groupId={data?.groupable_id ?? 0}
          />
        ),
        title: t('events.add_report'),
        width: 800,
      })
    );
  };

  const onDelete = () => {
    deleteReport(reportId ?? 0)
      .unwrap()
      .then(() => {
        props.onDeleted?.();
      });
  };

  const showButtons = useMemo(() => {
    return data?.user_id === user?.id || userIsAdmin || userIsCoach;
  }, [data?.user_id, user?.id, userIsAdmin, userIsCoach]);

  return (
    <Skeleton loading={isFetching} active title paragraph={{ rows: 8 }}>
      <PageHeader
        style={{ marginTop: 24, marginBottom: 0 }}
        title={data?.title}
        extra={
          showButtons && (
            <Button.Group>
              <Button icon={<EditOutlined />} onClick={() => onEdit()} />
              <Popconfirm
                title={t('delete') + '?'}
                onConfirm={() => onDelete()}
                disabled={isDeleting}
              >
                <Button icon={<DeleteOutlined />} danger loading={isDeleting} />
              </Popconfirm>
            </Button.Group>
          )
        }
      />
      <Typography.Text>
        {data?.user && <UserAvatar user={data?.user} />}{' '}
        {fullUserName(data?.user)} |{' '}
        {formatDateTime(data?.created_at ?? '', { withHypens: true })}
      </Typography.Text>
      <Divider />
      <Typography.Paragraph>
        {HtmlParser(data?.text ?? '')}
      </Typography.Paragraph>
    </Skeleton>
  );
}
